<script setup lang="ts">
import { partsDuPanier } from "~/utils/plusieursFois";
import { useDemoAccueil } from "~/composables/useDemoAccueil";
import { remonterAChaqueEtape } from "~/utils/defilementEtape";
import { getDonoorTipAmountAchat } from "@donoor/utils";
import { resolveCardProvider } from "@donoor/checkout";
/**
 * Page publique d'un événement
 * UX inspirée de HelloAsso : bannière full-width, 2 colonnes, card info + billets inline
 */
import type { EventAttendeeLocal } from "~/composables/useEventCheckoutValidation";
import { usePaymentFormLifecycle } from "~/composables/payment/usePaymentFormLifecycle";
import { fournirPaysAssociation } from "~/composables/usePaysAssociation";
import { useEventChatContext } from "~/composables/useEventChatContext";

const { sanitize } = useSanitizeHtml();
const { t } = useI18n();

definePageMeta({ layout: false });

const route = useRoute();
// Démo de la home (useDemoAccueil) : la page y est affichée telle quelle, son
// slug vient de la démo (l'adresse est celle de la home).
const demoAccueil = useDemoAccueil();
const slug = computed(
  () =>
    demoAccueil.integree?.slug ??
    ((route.params as Record<string, string | string[] | undefined>).slug as string),
);
const isPreview = computed(() => route.query.preview === "true");

// Mode widget embarqué (?embed=1) : masque l'habillage (banner/header/sidebar/
// footer), fond transparent, auto-resize vers l'hôte. Le tunnel est inchangé.
const embedRootEl = ref<HTMLElement | null>(null);
const { isEmbed, hostOrigin } = useEmbedFrame(embedRootEl);

// Fetch SSR + disponibilité (404 canceled/archived/draft/not_found → vraie
// raison) — composable dédié.
const { event, isInitialLoading, isEntityMissing, missingCopy } =
  await useEventPublicData(slug, isPreview);

// Pays de l'association : les étapes du tunnel y lisent les téléphones.
fournirPaysAssociation(() => event.value?.country);

// Vrai statut HTTP 404 côté SSR quand l'événement n'existe pas (404 API
// explicite uniquement). Google reçoit le bon signal ; l'humain voit la page
// « introuvable » + suggestions. Audit SEO 2026-08-01.
// Jamais dans la démo de la home : c'est la home qui recevrait le 404.
if (import.meta.server && isEntityMissing.value && !demoAccueil.estIntegree) {
  const requestEvent = useRequestEvent();
  if (requestEvent) setResponseStatus(requestEvent, 404);
}

// Accent color + status (composables dédiés)
const { accentStyle } = useEventAccentColor(event);
const { isCompleted, isClosed, isEventEnded, eventEndedLabel, formattedDate, formattedEnd } = useEventStatus(event);

// Sold count overrides (temps réel)
const soldCountOverrides = ref<Record<string, number>>({});
const ticketTypes = computed(() => {
  const types = event.value?.ticket_types || [];
  if (Object.keys(soldCountOverrides.value).length === 0) return types;
  return types.map(tt => {
    if (soldCountOverrides.value[tt.id] !== undefined) {
      // ⚠️ Temps réel (Karim 2026-06-23) : le socket ne pousse QUE sold_count.
      // On DOIT donc invalider remaining/sold_out figés au SSR, sinon
      // getRemainingForDisplay/isSoldOut les préfèrent et le badge « Complet »
      // ne se met JAMAIS à jour en live devant l'acheteur. En les remettant à
      // undefined, tout se recalcule à partir du sold_count live (max_quantity −
      // sold_count + capacité globale) → badge, opacité, clamp réactif réagissent.
      return {
        ...tt,
        sold_count: soldCountOverrides.value[tt.id],
        remaining: undefined,
        sold_out: undefined,
      };
    }
    return tt;
  });
});
const ticketOptions = computed(() => event.value?.ticket_options || []);


// SEO + JSON-LD (composable dédié)
const { eventCoverImageId } = useEventCheckoutSeo(event, slug);

// Widget intégré : petite vignette de l'événement dans l'en-tête compact
// (Karim 2026-10-29 : « trop vide » — on ne savait pas pour quel événement on
// prenait un billet). Directus redimensionne à la volée, comme la bannière.
const adresseApi = (useRuntimeConfig().public.apiUrl as string) || "https://api.donoor.org";
const vignetteIntegration = computed(() =>
  eventCoverImageId.value
    ? `${adresseApi}/api/assets/${eventCoverImageId.value}?width=160&height=160&fit=cover&format=webp&quality=80`
    : null,
);

// Attendees (déclaré tôt — utilisé par useEventTicketSelection).
// Type partagé avec useEventCheckoutValidation (une entrée par billet).
const attendees = ref<EventAttendeeLocal[]>([]);

// === Code promo (placé avant ticket selection car le calcul totalPrice dépend du promoDiscount) ===
// Note : on définit ticketsOnlyPrice / optionsTotal d'abord en stub puis on réassigne après pour casser la dépendance circulaire.
const ticketsOnlyPriceRef = ref(0);
const optionsTotalRef = ref(0);
const { promoCode, promoLoading, promoError, promoApplied, promoDiscount, applyPromoCode, removePromo } =
  useEventPromoCode(slug, ticketTypes, ticketsOnlyPriceRef, optionsTotalRef);

// Ticket selection (composable dédié)
const {
  selections,
  flexiblePrices,
  flexibleSuggestions,
  getOptionsForTicketType,
  totalRemainingPlaces,
  effectiveCapacity,
  hasAvailableTickets,
  totalSelected,
  getRemainingForDisplay,
  isSoldOut,
  getMaxForTicketType,
  increment,
  decrement,
  setFlexiblePrice,
  ticketsOnlyPrice,
  optionsTotal,
  totalPrice,
} = useEventTicketSelection({
  event,
  ticketTypes,
  ticketOptions,
  attendees: attendees as unknown as Ref<{ selectedOptions: Record<string, number> }[]>,
  promoDiscount,
});

// Sync ticketsOnlyPrice et optionsTotal vers les refs utilisées par le promo (évite la dépendance circulaire avec totalPrice).
watchEffect(() => { ticketsOnlyPriceRef.value = ticketsOnlyPrice.value; });
watchEffect(() => { optionsTotalRef.value = optionsTotal.value; });

// Animated counters (temps réel — composable dédié)
const toast = useToast();
const { animatedGlobalRemaining } = useEventAnimatedCounters({
  totalRemainingPlaces,
  ticketTypes,
  getRemainingForDisplay,
});

// Waitlist
const isWaitlistEnabled = computed(() => !!event.value?.waitlist_enabled);
const orderCompleted = ref(false);
// Pas de liste d'attente si la billetterie est clôturée (plus de vente du
// tout → on affiche la carte « Billetterie clôturée », pas un waitlist).
// Karim 2026-06-27.
const showWaitlist = computed(() => !hasAvailableTickets.value && isWaitlistEnabled.value && !isEventEnded.value && !isClosed.value && !orderCompleted.value);

// Contexte donné à l'IA du chat (lieu, ventes, places restantes…).
useEventChatContext(event, {
  ticketTypes,
  isEventEnded,
  isClosed,
  totalRemainingPlaces,
  hasAvailableTickets,
  isWaitlistEnabled,
});
const { waitlistForm, waitlistLoading, waitlistMessage, joinWaitlist } = useEventWaitlist(slug);

// Checkout steps
const checkoutStep = ref(1);
const payerIndex = ref<number | null>(0);

// Socket realtime (toast + conflict detection)
useEventTicketSocket({
  event,
  ticketTypes,
  selections,
  totalSelected,
  checkoutStep,
  soldCountOverrides,
});

const buyerInfo = ref({ firstName: "", lastName: "", email: "", confirmEmail: "", phone: "" });
const eventCustomFields = computed(() => event.value?.custom_fields || []);
const isProcessing = ref(false);
const orderError = ref<string | null>(null);
const orderErrorTechnical = ref(false);

// Modal de progression paiement (composable partagé avec /a/[slug])
const { processingStep, processingProgress, startProgress, stopProgress } =
  useCheckoutProcessingProgress();

// Payment providers state (composable mutualisé)
const {
  stripeAccountId,
  paymentProviders,
  stripeChargesEnabled,
  paypalChargesEnabled,
  selectedProvider,
  isPaypalOnlyMode,
  preferredProvider,
  paypalCardFieldsEnabled,
} = usePaymentProvidersState(event);

// Provider du FORMULAIRE CARTE (Karim 2026-08-30) : `preferred_provider` = où
// l'asso veut recevoir l'argent (bouton PayPal, wallets), mais il n'impose les
// CardFields PayPal que si PayPal sait les rendre (capability PPCP_CUSTOM).
// Sinon, Stripe actif → carte Stripe. Jamais de cadre vide. Règle unique dans
// `resolveCardProvider` (@donoor/checkout) — ne PAS la réimplémenter ici.
const usesPaypalCard = computed(
  () =>
    resolveCardProvider({
      stripe: stripeChargesEnabled.value,
      paypal: paypalChargesEnabled.value,
      paypalCardFields: paypalCardFieldsEnabled.value,
      preferred: preferredProvider.value,
    }) === "paypal",
);
const usesPaypalWallets = computed(
  () => isPaypalOnlyMode.value || usesPaypalCard.value,
);
const showPayPalAlternative = computed(
  () => stripeChargesEnabled.value && paypalChargesEnabled.value,
);

const isPaymentConfigured = computed(() => paymentProviders.value.accepts_payments);
const donoorFeeRate = computed(() => (event.value?.donoor_fee_rate ?? 0) / 100);
const isTaxDeductible = computed(() => event.value?.is_tax_deductible === true);
const hasFees = computed(() => donoorFeeRate.value > 0);

const includeFees = ref(true);
const donateToUsAmount = ref<number>(0);
const donateUs = ref(true);
const donationAmount = ref<number>(0);
// Même lecture que l'éditeur d'événement (dashboard, iOS, Android) et que le
// serveur : le don est permis sauf refus explicite.
const showDonation = computed(() => event.value?.allow_donation !== false);
const showDonationInput = ref(false);

// Le soutien Donoor est suggéré à partir du montant AVANT remise, JAMAIS du
// total remisé : un code promo ne doit pas réduire ce qui revient à Donoor.
// Commande 10 € + 2 € de soutien avec un code 100 % → 0 € pour l'asso, 2 €
// pour Donoor. Et dès que l'acheteur a touché au curseur, on ne réécrit plus
// sa valeur. Karim 2026-08-07.
const preDiscountTotal = computed(
  () => ticketsOnlyPrice.value + optionsTotal.value + donationAmount.value,
);
const donateToUsTouched = ref(false);
watch(preDiscountTotal, (base) => {
  if (donateToUsTouched.value) return;
  // Achat : suggestion plafonnée à 10 € (cf. @donoor/utils tip.ts).
  donateToUsAmount.value = getDonoorTipAmountAchat(base);
}, { immediate: true });

function setDonateToUsAmount(value: number) {
  donateToUsTouched.value = true;
  donateToUsAmount.value = value;
}

const { estimateProvider, paypalFeePercent, paypalFeeFixed } =
  usePaypalFeeEstimate(paymentProviders, ref(false));
const calculations = usePaymentCalculations({
  // Billets seuls : le DON à l'association passe à part (`donAssociation`),
  // débité sans commission Donoor — exactement comme le serveur.
  amount: computed(() => String(totalPrice.value)),
  donAssociation: donationAmount,
  includeFees,
  donoorFeeRate,
  isSubscription: ref(false),
  subscriptionPeriod: ref("monthly"),
  donateToUsAmount,
  paymentProvider: estimateProvider,
  paypalFeePercent,
  paypalFeeFixed,
  // Le SERVEUR fixe le montant débité : on reproduit son arrondi à
  // l'identique, sinon affiché ≠ débité d'un centime (audit 2026-09-23).
  arrondiServeur: true,
});


// === useCheckout ===

// Paiement en plusieurs fois (0 = en une fois) — Karim 2026-10-21.
const nombreParts = ref(0);

const { buildOrderPayload } = useEventOrderPayload({
  nombreParts,
  ticketTypes,
  selections,
  flexiblePrices,
  attendees,
  buyerInfo,
  includeFees,
  donateUs,
  donateToUsAmount,
  donationAmount,
  selectedProvider,
  promoApplied,
});

// Instances de paiement Stripe + PayPal + wallets (composable dédié)
const {
  checkout,
  paypalCheckout,
  mountPrimaryWallets,
  setPendingStripeOrder,
  clearPendingOrders,
  abandonPendingOrder,
} =
  useEventCheckoutProviders({
    slug,
    isPreview,
    event,
    stripeAccountId,
    paymentProviders,
    isPaypalOnlyMode,
    usesPaypalWallets,
    selectedProvider,
    includeFees,
    calculations,
    buyerInfo,
    buildOrderPayload,
  });

// Validations des steps (composable dédié — pendant de /a)
const { allAttendeesComplete, coordsValid, canGoToStep } =
  useEventCheckoutValidation({
    attendees,
    eventCustomFields,
    getOptionsForTicketType,
    buyerInfo,
    totalSelected,
    checkoutStep,
  });

function goToParticipants() {
  if (totalSelected.value === 0) return;

  // Pre-flight disponibilité : un tarif a pu se remplir entre la sélection et
  // ce clic (live socket, ou sold_count stale au chargement). On vérifie chaque
  // ligne contre les places restantes effectives. Si un tarif est complet ou
  // dépassé, on bloque (le clamp réactif a déjà réduit la quantité) avec un
  // message clair, sans avancer. Karim 2026-06-23.
  for (const [id, qty] of Object.entries(selections.value)) {
    if ((qty as number) <= 0) continue;
    const tt = ticketTypes.value.find(t => t.id === id);
    if (!tt) continue;
    const remaining = getRemainingForDisplay(tt);
    if (isSoldOut(tt) || (remaining !== null && (qty as number) > remaining)) {
      toast.add({
        title: t("billetterie.pageEvenement.plusAssezDePlaces", { nom: tt.name }),
        description: t("billetterie.partage.selectionAjustee"),
        color: "warning",
        duration: 6000,
      });
      return;
    }
  }

  const list: EventAttendeeLocal[] = [];
  for (const [id, qty] of Object.entries(selections.value)) {
    const tt = ticketTypes.value.find(t => t.id === id);
    for (let i = 0; i < (qty as number); i++) {
      list.push({ firstName: "", lastName: "", email: "", custom_data: {}, ticketTypeId: id, ticketTypeName: tt?.name || t("billetterie.pageEvenement.billetParDefaut"), selectedOptions: {} });
    }
  }
  attendees.value = list;
  payerIndex.value = 0;
  checkoutStep.value = 2;
}

function togglePayer(idx: number) {
  payerIndex.value = payerIndex.value === idx ? null : idx;
}

function goToCoordonnees() {
  if (!allAttendeesComplete.value) return;
  if (payerIndex.value !== null) {
    const payer = attendees.value[payerIndex.value];
    if (payer) {
      buyerInfo.value.firstName = payer.firstName;
      buyerInfo.value.lastName = payer.lastName;
      buyerInfo.value.email = payer.email;
      buyerInfo.value.confirmEmail = payer.email;
    }
  }
  checkoutStep.value = 3;
}

async function goToRecap() {
  if (!coordsValid.value) return;
  checkoutStep.value = 4;
}


const wizardTopRef = remonterAChaqueEtape(checkoutStep);

function goToStep(step: number) {
  if (!canGoToStep(step)) return;
  if (step === 4) {
    goToRecap();
  } else {
    checkoutStep.value = step;
  }
}

const needsPayment = computed(() =>
  totalPrice.value > 0 || donationAmount.value > 0 || (showDonation.value && showDonationInput.value) || (donateUs.value && donateToUsAmount.value > 0),
);

/**
 * Formulaire de paiement (carte + wallets) : préparé tant que le bloc paiement
 * est À L'ÉCRAN (récap + quelque chose à payer), rangé sinon.
 *
 * ⚠️ Avant le 2026-09-25, il était préparé en ARRIVANT sur le récap : sur un
 * événement gratuit (0 €, bloc caché), la préparation échouait ; le pourboire
 * ajouté ensuite affichait le bloc sans carte ni bouton PayPal (prod, Team
 * Elite Pétanque). Cf. `usePaymentFormLifecycle`.
 */
const paiement = usePaymentFormLifecycle({
  visible: computed(
    () => checkoutStep.value === 4 && !isPreview.value && needsPayment.value,
  ),
  monter: async () => {
    // Carte selon le prestataire PRIMAIRE (Karim 2026-06-21) :
    //   usesPaypalCard → PayPal CardFields ; sinon → Stripe Elements.
    const carteOk = usesPaypalCard.value
      ? await paypalCheckout.mountCardForm("paypal-cardfields-container")
      : await checkout.mountCardForm("card-element", { type: "card-legacy" });
    // Wallets — route Stripe Express OU PayPal Apple+Google.
    await mountPrimaryWallets();
    return carteOk;
  },
  demonter: () => {
    checkout.destroy();
    paypalCheckout.destroy();
  },
  cle: () => usesPaypalCard.value,
});

function retryStripeInit() {
  void paiement.reessayer();
}


// === Checkout session save/restore ===
const {
  sessionExpiredModal,
  dismissSessionExpired,
  saveCheckoutState,
  restoreCheckoutState,
  clearCheckoutSession,
} = useEventCheckoutSession(
  slug,
  checkoutStep,
  {
    selections,
    flexiblePrices,
    attendees: attendees as unknown as Ref<unknown[]>,
    buyerInfo: buyerInfo as unknown as Ref<unknown>,
    payerIndex,
    includeFees,
    donateUs,
    donationAmount,
    showDonationInput,
    promoApplied: promoApplied as unknown as Ref<unknown>,
    ticketTypes,
  },
  () => ({
    selections: selections.value,
    flexiblePrices: flexiblePrices.value,
    attendees: attendees.value,
    buyerInfo: buyerInfo.value,
    payerIndex: payerIndex.value,
    includeFees: includeFees.value,
    donateUs: donateUs.value,
    donationAmount: donationAmount.value,
    showDonationInput: showDonationInput.value,
    promoApplied: promoApplied.value,
  }),
);

watch([checkoutStep, selections, attendees, buyerInfo, payerIndex, includeFees, donationAmount], () => {
  saveCheckoutState();
}, { deep: true });

/**
 * Après restauration de session : si on atterrit DIRECTEMENT sur l'étape récap
 * (refresh sur `?step=4`), il faut monter le formulaire de paiement — sinon il
 * n'est jamais initialisé (mountCardForm n'était appelé qu'à la transition
 * step 3→4 via goToRecap) → « Chargement du système de paiement… » infini.
 */
function restoreAndMountIfOnRecap() {
  // Le paiement se prépare tout seul si l'on atterrit sur le récap (état
  // restauré) : cf. `usePaymentFormLifecycle`.
  restoreCheckoutState();
}

onMounted(() => {
  if (event.value && ticketTypes.value.length > 0) {
    restoreAndMountIfOnRecap();
  } else {
    const stop = watch([event, ticketTypes], () => {
      if (event.value && ticketTypes.value.length > 0) {
        restoreAndMountIfOnRecap();
        stop();
      }
    });
  }
});

// === Submit ===
const { submitOrder, clearOrderError, dismissProcessingError } = useEventOrderSubmit({
  slug,
  isPreview,
  isProcessing,
  needsPayment,
  selectedProvider,
  checkout,
  paypalCheckout,
  usesPaypalCard,
  orderCompleted,
  processingStep,
  processingProgress,
  orderError,
  orderErrorTechnical,
  promoApplied,
  buyerInfo,
  calculations,
  setPendingOrderResult: setPendingStripeOrder,
  clearPendingOrders,
  abandonPendingOrder,
  clearCheckoutSession,
  buildOrderPayload,
  startProgress,
  stopProgress,
  ticketTypes,
  getRemainingForDisplay,
});

// === Libération immédiate de la place à l'abandon (Karim 2026-09-06) ===
// Une commande `pending` créée puis abandonnée est annulée dès que l'acheteur
// quitte le tunnel : navigation interne (retour, autre page) ou fermeture de
// l'onglet (`pagehide` + fetch keepalive). Sans effet si la commande est payée
// (oubliée à `markOrderSuccess`) ou confiée à la page PayPal.
onBeforeRouteLeave(() => {
  void abandonPendingOrder();
});
function libererSurFermeture() {
  void abandonPendingOrder({ keepalive: true });
}
onMounted(() => window.addEventListener("pagehide", libererSurFermeture));
onBeforeUnmount(() => window.removeEventListener("pagehide", libererSurFermeture));

const showShareModal = ref(false);

// Paiement en plusieurs fois choisi : les parts (calcul du serveur), pour le
// récapitulatif de droite — total réel, à payer aujourd'hui, puis la suite.
const partsPlusieursFois = computed(() =>
  partsDuPanier({
    nombre: nombreParts.value,
    montantDiviseEuros: totalPrice.value,
    enPlusEuros: donationAmount.value,
    soutienEuros: donateUs.value ? donateToUsAmount.value : 0,
    tauxCommission: donoorFeeRate.value ?? 0,
    fraisCouverts: includeFees.value,
  }),
);
</script>


<template>
  <div
    ref="embedRootEl"
    :class="isEmbed ? 'bg-transparent @container p-4 sm:p-5' : ((!isInitialLoading && isEntityMissing) ? 'min-h-screen flex flex-col bg-white dark:bg-[#0a0a0a]' : 'min-h-screen flex flex-col bg-[#f7f9fd] dark:bg-[#0a0a0a]')"
    :style="accentStyle"
  >
    <!-- Surveillance : asso suspended/banned.
         `min-h-screen` + fond : sans eux, le conteneur racine s'arrêtait à la
         hauteur du bloc et laissait une coupure blanche sous la page (le fond
         `bg-[#f7f9fd]` ne couvrait pas la fenêtre entière).
         Suggestions + footer : la page ne doit pas être un cul-de-sac, même
         raison que la branche 404 juste en dessous (Karim 2026-08-01). -->
    <div
      v-if="event && (event.organization_risk_status === 'suspended' || event.organization_risk_status === 'banned')"
      :class="isEmbed ? '' : 'min-h-screen flex flex-col bg-[#f7f9fd] dark:bg-[#0a0a0a]'"
    >
      <AppHeader v-if="!isEmbed && !demoAccueil.estIntegree" rayon="evenements" />
      <div class="flex-1">
        <OrgUnavailable
          :risk-status="event.organization_risk_status"
          :reason="event.organization_risk_reason"
          :organization-name="event.organization_name"
        />
        <div v-if="!isEmbed" class="max-w-6xl mx-auto px-4 pb-16">
          <NotFoundSuggestions
            alternative type="evenements" />
        </div>
      </div>
      <FooterLite v-if="!isEmbed && !demoAccueil.estIntegree" class="mt-0!" />
    </div>

    <PublicPageLoadingSkeleton v-else-if="isInitialLoading || (!event && !isEntityMissing)" rayon="evenements" />

    <PublicPageErrorState
      v-else-if="isEntityMissing"
      rayon="evenements"
      :icon="missingCopy.icon"
      :title="missingCopy.title"
      :message="missingCopy.message"
    >
      <!-- Événement supprimé/inexistant → proposer des événements ouverts
           (Karim 2026-08-01 : la page 404 ne doit pas être un cul-de-sac). -->
      <template #suggestions>
        <NotFoundSuggestions type="evenements" />
      </template>
    </PublicPageErrorState>

    <template v-else-if="event">
      <!-- Header en overlay (position fixed) au-dessus de la banner pour ne pas casser l'image -->
      <AppHeader v-if="!isEmbed && !demoAccueil.estIntegree" :links="navLinks" rayon="evenements" />

      <div v-if="isCompleted" class="bg-gray-900 text-white text-center text-sm font-medium py-3 px-4">
        <UIcon name="i-lucide-trophy" class="size-4 inline -mt-0.5 mr-1" />
        {{ t('billetterie.pageEvenement.evenementTermine') }}
      </div>
      <!-- Billetterie clôturée (status 'closed') : PAS de bannière — l'info
           est déjà affichée dans le bloc billets (« Billetterie clôturée »).
           Karim 2026-07-02. -->
      <div v-if="isPreview" class="bg-amber-500 text-white text-center text-sm font-medium py-2 px-4">
        {{ t('billetterie.pageEvenement.modeApercu') }}
      </div>

      <BslugBanner
        v-if="!isEmbed"
        :cover-image-id="eventCoverImageId"
        :title="event.title"
        :organization-logo="event?.organization_logo"
        :organization-name="event?.organization_name"
      />

      <!-- ⚠️ REMONTE PAR-DESSUS LA BANNIÈRE FIXE (Karim 2026-08-23, même
           traitement que la boutique). `-mt-[68px]` couvre l'écart PLUS les
           28px du rayon : sans ça l'arrondi ne se détache sur rien au repos.
           `z-10` passe au-dessus de la bannière (`z-0`).
           DEUX niveaux : le FOND arrondi couvre toute la largeur, le CONTENU
           reste borné à 1280px. Un seul div ne peut pas faire les deux.
           `pb-10` : l'écart avec le footer est DANS ce panneau, pas en marge
           du footer — une marge laissait une bande vide où la bannière fixe
           transparaissait en bas de page sur petit écran (Karim 2026-10-07). -->
      <div
        :class="isEmbed
          ? 'w-full'
          : 'relative z-10 grow -mt-[68px] rounded-t-[28px] bg-[#f7f8fa] dark:bg-[#0a0a0a] pt-8 pb-10'"
      >
      <div :class="isEmbed ? 'w-full' : 'w-full max-w-[1280px] mx-auto px-3 md:px-6'">
        <div class="flex flex-col lg:flex-row gap-4 md:gap-6 mt-0">

          <!-- LEFT COLUMN (70%) -->
          <div
            class="flex-1 min-w-0 space-y-4 md:space-y-6"
            :class="isEmbed ? 'lg:w-full mt-0' : 'lg:w-3/4 mt-4 md:mt-6'"
          >

            <BslugHeaderCard
              v-if="!isEmbed"
              :event="event"
              :formatted-date="formattedDate"
              :formatted-end="formattedEnd"
              :description-html="sanitize(event.description || '')"
              @open-share="showShareModal = true"
            />

            <!-- Widget intégré : en-tête compact (vignette, titre, date) à la
                 place de la grande carte de la page publique. -->
            <div v-if="isEmbed" class="flex items-center gap-3">
              <img
                v-if="vignetteIntegration"
                :src="vignetteIntegration"
                alt=""
                width="56"
                height="56"
                class="size-14 shrink-0 rounded-2xl object-cover"
              >
              <div class="min-w-0">
                <p class="truncate text-base font-bold leading-tight text-[#2d2e5c] dark:text-white">{{ event.title }}</p>
                <p class="mt-1 flex items-center gap-1.5 text-xs font-medium text-[#717198] dark:text-gray-400">
                  <UIcon name="i-lucide-calendar-days" class="size-3.5 shrink-0" />
                  <span class="truncate">{{ formattedDate }}</span>
                </p>
              </div>
            </div>

            <PublicPageTaxDeductibleBanner v-if="!isEmbed && isTaxDeductible" :label="t('billetterie.pageEvenement.reductionImpot66')" />

            <PublicPageYoutubeEmbed v-if="!isEmbed && event.youtube_url" :url="event.youtube_url" />

            <!-- Event ended -->
            <div v-if="isEventEnded" class="rounded-3xl overflow-hidden bg-[#FFF3C4] dark:bg-[#3D3520] p-8">
              <div class="flex flex-col items-center justify-center text-center py-4">
                <img
                  src="/svg/hands-check.svg" alt="" class="w-24 h-24 mb-5"
                  width="246" height="211"
                />
                <h3 class="text-xl font-bold text-[#1E1E1E] dark:text-white mb-2">
                  {{ t('billetterie.pageEvenement.evenementEtat', { etat: eventEndedLabel }) }}
                </h3>
                <p class="text-sm text-[#5C5030] dark:text-gray-400 max-w-xs">
                  {{ event?.status === 'canceled'
                    ? t('billetterie.pageEvenement.evenementAnnuleMessage')
                    : event?.status === 'completed'
                      ? t('billetterie.pageEvenement.evenementTermineMessage')
                      : t('billetterie.pageEvenement.evenementPasseMessage') }}
                </p>
              </div>
            </div>

            <BslugWaitlistCard
              v-else-if="showWaitlist"
              v-model="waitlistForm"
              :message="waitlistMessage"
              :loading="waitlistLoading"
              @submit="joinWaitlist"
            />

            <BslugStatusNotice
              v-else-if="isCompleted"
              icon="i-lucide-trophy"
              tone="gray"
              :title="t('billetterie.pageEvenement.evenementTermineTitreNotice')"
              :message="t('billetterie.pageEvenement.evenementTermineMessageNotice')"
            />

            <!-- Billetterie clôturée : carte dédiée À LA PLACE du checkout
                 (achat masqué). La page reste consultable. Karim 2026-06-27. -->
            <BslugStatusNotice
              v-else-if="isClosed"
              icon="i-lucide-lock"
              tone="amber"
              :title="t('billetterie.pageEvenement.billetterieClotureeTitre')"
              :message="t('billetterie.pageEvenement.billetterieClotureeMessage')"
            />

            <!-- ⚠️ PAS en aperçu (Karim 2026-09-10) : sans moyen de paiement
                 configuré, l'aperçu affichait ce bloc À LA PLACE du tunnel —
                 or c'est justement le tunnel que l'organisation vient
                 vérifier. Le paiement reste désactivé en aperçu, le bandeau
                 ambré de BslugStepRecap le dit déjà. -->
            <PublicPagePaymentUnavailable v-else-if="!isPaymentConfigured && !isPreview" />

            <!-- Checkout Flow -->
            <div
              v-else
              ref="wizardTopRef"
              class="bg-white dark:bg-[#141414] rounded-3xl shadow-[0_1px_3px_rgba(45,46,92,0.12)] overflow-hidden scroll-mt-4"
            >
              <div class="flex items-center border-b border-gray-100 dark:border-gray-800">
                <button
                  v-for="(label, idx) in [
                    t('billetterie.pageEvenement.ongletBillets'),
                    t('billetterie.pageEvenement.ongletParticipants'),
                    t('billetterie.pageEvenement.ongletCoordonnees'),
                    t('billetterie.pageEvenement.ongletRecapitulatif'),
                  ]"
                  :key="idx"
                  class="flex-1 flex items-center justify-center gap-1.5 px-1 py-3 md:py-4 text-xs md:text-sm font-semibold transition-all relative"
                  :class="checkoutStep === idx + 1
                    ? 'text-[#212121] dark:text-white'
                    : canGoToStep(idx + 1)
                      ? 'text-[#212121] dark:text-white cursor-pointer hover:bg-gray-50 dark:hover:bg-[#1a1a1a]'
                      : 'text-gray-300 dark:text-gray-600 cursor-not-allowed'"
                  :disabled="!canGoToStep(idx + 1)"
                  @click="goToStep(idx + 1)"
                >
                  <div
                    class="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 transition-colors"
                    :class="checkoutStep >= idx + 1 ? '' : 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500'"
                    :style="checkoutStep >= idx + 1 ? { background: 'var(--accent, #212121)', color: 'var(--accent-text, #fff)' } : undefined"
                  >
                    <UIcon v-if="checkoutStep > idx + 1" name="i-lucide-check" class="size-3.5" />
                    <span v-else>{{ idx + 1 }}</span>
                  </div>
                  <span class="hidden sm:inline">{{ label }}</span>
                  <div v-if="checkoutStep === idx + 1" class="absolute bottom-0 left-4 right-4 h-0.5 rounded-full" :style="{ background: 'var(--accent, #212121)' }" />
                </button>
              </div>

              <Transition
                mode="out-in"
                enter-active-class="transition-all duration-300 ease-out"
                enter-from-class="opacity-0 translate-y-2"
                enter-to-class="opacity-100 translate-y-0"
                leave-active-class="transition-all duration-150 ease-in"
                leave-from-class="opacity-100 translate-y-0"
                leave-to-class="opacity-0 -translate-y-2"
              >
                <BslugStepTickets
                  v-if="checkoutStep === 1"
                  key="step1"
                  :ticket-types="ticketTypes"
                  :selections="selections"
                  :flexible-prices="flexiblePrices"
                  :total-selected="totalSelected"
                  :total-price="totalPrice"
                  :promo-discount="promoDiscount"
                  :total-remaining-places="totalRemainingPlaces"
                  :effective-capacity="effectiveCapacity"
                  :animated-global-remaining="animatedGlobalRemaining"
                  :get-remaining-for-display="getRemainingForDisplay"
                  :is-sold-out="isSoldOut"
                  :get-max-for-ticket-type="getMaxForTicketType"
                  :get-options-for-ticket-type="(id: string) => getOptionsForTicketType(id)"
                  :flexible-suggestions="flexibleSuggestions"
                  @increment="increment"
                  @decrement="decrement"
                  @update:flexible-price="setFlexiblePrice"
                  @continue="goToParticipants"
                />

                <BslugStepAttendees
                  v-else-if="checkoutStep === 2"
                  key="step2"
                  :attendees="attendees"
                  :payer-index="payerIndex"
                  :event-custom-fields="eventCustomFields"
                  :get-options-for-ticket-type="getOptionsForTicketType"
                  :all-attendees-complete="allAttendeesComplete"
                  @toggle-payer="togglePayer"
                  @back="checkoutStep = 1"
                  @continue="goToCoordonnees"
                />

                <BslugStepCoordonnees
                  v-else-if="checkoutStep === 3"
                  key="step3"
                  v-model="buyerInfo"
                  @back="checkoutStep = 2"
                  @continue="goToRecap"
                />

                <BslugStepRecap
                  v-else-if="checkoutStep === 4"
                  key="step4"
                  :event="event"
                  :attendees="attendees"
                  :ticket-types="ticketTypes"
                  :ticket-options="ticketOptions"
                  :total-price="totalPrice"
                  :tickets-only-price="ticketsOnlyPrice"
                  :options-total="optionsTotal"
                  :promo-code="promoCode"
                  :promo-loading="promoLoading"
                  :promo-error="promoError"
                  :promo-applied="promoApplied"
                  :promo-discount="promoDiscount"
                  :show-donation="showDonation"
                  :show-donation-input="showDonationInput"
                  :donation-amount="donationAmount"
                  :include-fees="includeFees"
                  :donate-us="donateUs"
                  :donate-to-us-amount="donateToUsAmount"
                  :has-fees="hasFees"
                  :calculations="calculations"
                  :is-preview="isPreview"
                  :needs-payment="needsPayment"
                  :stripe-checkout="checkout"
                  :paypal-checkout="paypalCheckout"
                  :payment-form="{ isSubscription: ref(false) }"
                  :uses-paypal-card="usesPaypalCard"
                  :uses-paypal-wallets="usesPaypalWallets"
                  :is-paypal-only-mode="isPaypalOnlyMode"
                  :show-pay-pal-alternative="showPayPalAlternative"
                  :stripe-charges-enabled="stripeChargesEnabled"
                  :paypal-charges-enabled="paypalChargesEnabled"
                  :checkout-is-initialized="checkout.cardMounted.value"
                  :checkout-error="checkout.error.value"
                  :order-error="orderError"
                  :order-error-technical="orderErrorTechnical"
                  :is-processing="isProcessing"
                  :nombre-parts="nombreParts"
                  :taux-commission="donoorFeeRate"
                  @update:nombre-parts="nombreParts = $event"
                  @update:show-donation-input="showDonationInput = $event"
                  @update:donation-amount="donationAmount = $event"
                  @update:include-fees="includeFees = $event"
                  @update:donate-to-us-amount="setDonateToUsAmount($event)"
                  @update:promo-code="promoCode = $event"
                  @apply-promo="applyPromoCode"
                  @remove-promo="removePromo"
                  @retry-stripe-init="retryStripeInit"
                  @clear-order-error="clearOrderError"
                  @back="checkoutStep = 3"
                  @submit="submitOrder"
                />
              </Transition>
            </div>

            <PublicPageSecurityBadges v-if="!isEmbed" />
            <!-- Embed : « Propulsé par Donoor.org » en bas, centré — parité
                 boutique (Karim 31/08). -->
            <div v-if="isEmbed" class="mt-5 mb-1 flex justify-center">
              <EmbedPoweredBy type="event" :host-origin="hostOrigin" />
            </div>
          </div>

          <BslugSidebar
            v-if="!isEmbed"
            :event="event"
            :checkout-step="checkoutStep"
            :total-selected="totalSelected"
            :ticket-types="ticketTypes"
            :selections="selections"
            :attendees="attendees"
            :ticket-options="ticketOptions"
            :tickets-only-price="ticketsOnlyPrice"
            :options-total="optionsTotal"
            :promo-applied="promoApplied"
            :promo-code="promoCode"
            :promo-loading="promoLoading"
            :promo-error="promoError"
            :promo-discount="promoDiscount"
            :donation-amount="donationAmount"
            :total-price="totalPrice"
            :fees-amount="calculations.feesAmount.value"
            :include-fees="includeFees"
            :donate-us="donateUs"
            :donate-to-us-amount="donateToUsAmount"
            :parts-plusieurs-fois="partsPlusieursFois"
            @update:promo-code="promoCode = $event"
            @apply-promo="applyPromoCode"
            @remove-promo="removePromo"
          />
        </div>
      </div>
      </div>

      <!-- Section « Découvrez d'autres événements » MASQUÉE (Karim 2026-08-06,
           pattern HelloAsso) : les assos ne veulent pas les events des autres
           sur leur page. Gardée en commentaire pour réactivation facile.
      <EventRelatedSection v-if="!isEmbed" :slug="slug" />
      -->

      <!-- Footer LÉGER (pattern HelloAsso, unifié avec /c). Karim 2026-08-06.
           Collé au panneau (`mt-0!`) : l'espace qui décolle le bandeau
           « Paiement sécurisé » du bord noir est le `pb-10` du panneau. -->
      <FooterLite v-if="!isEmbed && !demoAccueil.estIntegree" class="mt-0!" />
    </template>

    <EventShareModal v-model:open="showShareModal" :event="event" />

    <PublicPageSessionExpiredModal
      variant="b"
      :open="sessionExpiredModal"
      :message="t('billetterie.pageEvenement.sessionExpireeMessage')"
      @dismiss="dismissSessionExpired"
    />

    <BslugProcessingOverlay
      :open="isProcessing"
      :step="processingStep"
      :progress="processingProgress"
      :order-error="orderError"
      :order-error-technical="orderErrorTechnical"
      :contact-email="event?.contact_email"
      @dismiss-error="dismissProcessingError"
    />
  </div>
</template>

<style scoped>
.accent-btn {
  background: var(--accent, #212121);
  color: var(--accent-text, #fff);
}
.accent-btn:hover {
  opacity: 0.9;
}
.accent-bg {
  background: var(--accent, #212121);
  color: var(--accent-text, #fff);
}
.accent-switch :deep(button[data-state="checked"]) {
  background-color: var(--accent, #10b981) !important;
}
.hide-arrows::-webkit-outer-spin-button,
.hide-arrows::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.hide-arrows {
  -moz-appearance: textfield;
}
</style>
